/* ==========================================================================
   STICKR LAB — AI artwork landing page, CTA instrumentation
   (assets/sl-ai-landing.js)

   ── WHY THIS FILE EXISTS ────────────────────────────────────────────────
   sections/sl-landing-ai.liquid has shipped `data-sl-ai-cta` on all three
   calls to action since launch and nothing has ever read them. The page
   therefore had page views and no idea whether anyone reached the builder
   from it — which is the only question this page exists to answer.

   ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────
   It does not send a page_view. Shopify's Google & YouTube channel already
   emits one through its web pixel (GA4 G-BZLG6NHXC3, verified in the live
   page source), and a second one from here would double every session.

   It does not load gtag.js, GTM, or any tag of its own. Loading a second
   Google tag alongside the channel's is how you get duplicated events and
   an analytics property nobody trusts. This file only SPEAKS to whatever
   is already there.

   ── WHERE THE EVENTS GO, AND THE HONEST LIMIT ───────────────────────────
   Three sinks, all optional, all guarded:

     1. `window.dataLayer` — the standard queue. If a GTM container is ever
        added it will find these events already waiting, with no change here.
     2. `window.gtag` — called only if it is genuinely a function on this
        frame.
     3. a DOM CustomEvent `sl:ai-cta` on `document` — so anything else on
        the page can listen without this file knowing about it.

   THE LIMIT, STATED PLAINLY: the Google channel's gtag runs inside Shopify's
   web-pixel sandbox, not on this frame, so sink 2 is very unlikely to be
   present and sink 1 has no consumer today. Until a GTM container or a
   custom web pixel exists, these clicks are emitted but not collected.
   Wiring a sink is a separate, small project — see
   docs/seo/AI_ARTWORK_PAGE.md. This file is the half that belongs to the
   page, and it is written so that the other half is a configuration change
   rather than another code change.

   ── FAILURE POSTURE ─────────────────────────────────────────────────────
   Analytics must never break a page. Everything is inside try/catch, the
   listener is passive, and nothing here can cancel or delay a navigation.
   ========================================================================== */
(function (w, d) {
  'use strict';

  var PAGE = 'ai-art-stickers';
  var EVENT = 'sl_ai_cta';

  /* A click is only interesting once. Browsers can fire a click for the
     keyboard activation of an anchor as well, and a double-click on a CTA is
     one intent, not two. */
  var lastKey = '', lastAt = 0;

  function emit(cta, destination, href) {
    var now = (w.Date && Date.now) ? Date.now() : 0;
    var key = cta + '|' + destination;
    if (key === lastKey && now - lastAt < 800) return;
    lastKey = key; lastAt = now;

    var fields = {
      sl_page: PAGE,
      sl_cta: cta,
      sl_destination: destination,
      sl_href: href || ''
    };

    try {
      w.dataLayer = w.dataLayer || [];
      var forLayer = { event: EVENT };
      for (var k in fields) { if (fields.hasOwnProperty(k)) forLayer[k] = fields[k]; }
      w.dataLayer.push(forLayer);
    } catch (e) { /* analytics never breaks the page */ }

    try {
      if (typeof w.gtag === 'function') w.gtag('event', EVENT, fields);
    } catch (e) { /* ditto */ }

    try {
      d.dispatchEvent(new CustomEvent('sl:ai-cta', { detail: fields }));
    } catch (e) { /* CustomEvent constructor is absent on very old browsers */ }
  }

  /* `destination` is derived from the href rather than hard-coded per CTA, so
     a button that is later repointed reports where it now goes instead of
     where it went when this was written. */
  function destinationOf(href) {
    if (!href) return 'unknown';
    if (href.charAt(0) === '#') return 'section';
    if (href.indexOf('view=builder') !== -1) return 'builder';
    if (href.indexOf('make-your-own-stickers') !== -1) return 'builder';
    return 'page';
  }

  function onClick(e) {
    try {
      var el = e.target;
      /* No `closest` on the SVG arrow inside the button in older engines, so
         walk up by hand until the page root. */
      while (el && el !== d.documentElement) {
        if (el.getAttribute && el.getAttribute('data-sl-ai-cta')) {
          var href = (el.getAttribute && el.getAttribute('href')) || '';
          emit(el.getAttribute('data-sl-ai-cta'), destinationOf(href), href);
          return;
        }
        el = el.parentNode;
      }
    } catch (err) { /* never interfere with the click itself */ }
  }

  try {
    /* Scoped to the landing page's own root so this is inert if the asset is
       ever loaded somewhere it does not belong. */
    if (d.querySelector && d.querySelector('.sl-ai')) {
      d.addEventListener('click', onClick, true);
    }
  } catch (e) { /* no-op */ }
}(window, document));
